import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Grid,
  Paper,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Business,
  Work,
  Save,
} from '@mui/icons-material';

const Profile: React.FC = () => {
  const [formData, setFormData] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 234 567 8900',
    department: 'IT',
    position: 'Software Developer',
    bio: 'Experienced software developer with expertise in React and Laravel.',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Handle save logic here
    console.log('Profile saved:', formData);
  };

  return (
    <Box sx={{ 
      p: 3,
      backgroundColor: '#f0f9ff' // Light blue background
    }}>
      <Card sx={{ 
        borderRadius: '12px', 
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 600, 
            color: '#374151', 
            mb: 3
          }}>
            Profile Settings
          </Typography>

          <Grid container spacing={3}>
            {/* Profile Picture */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ 
                p: 3, 
                textAlign: 'center',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Avatar
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    mx: 'auto', 
                    mb: 2,
                    fontSize: '2rem',
                    backgroundColor: '#0d9488'
                  }}
                >
                  <Person sx={{ fontSize: '3rem' }} />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                  {formData.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                  {formData.position}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: '6px',
                    borderColor: '#0d9488',
                    color: '#0d9488',
                    '&:hover': {
                      borderColor: '#0f766e',
                      backgroundColor: '#f0fdfa',
                    },
                  }}
                >
                  Change Photo
                </Button>
              </Paper>
            </Grid>

            {/* Profile Form */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      InputProps={{
                        startAdornment: <Person sx={{ color: '#6b7280', mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      InputProps={{
                        startAdornment: <Email sx={{ color: '#6b7280', mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      InputProps={{
                        startAdornment: <Phone sx={{ color: '#6b7280', mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={formData.department}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      InputProps={{
                        startAdornment: <Business sx={{ color: '#6b7280', mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Position"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  InputProps={{
                    startAdornment: <Work sx={{ color: '#6b7280', mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Bio"
                  multiline
                  rows={4}
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#6b7280',
                      color: '#6b7280',
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#0d9488',
                      '&:hover': {
                        backgroundColor: '#0f766e',
                      },
                      px: 3,
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;